import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { ApiError } from 'src/api/axiosClient/types';
import { getCurrentCompanyId } from 'src/modules/companies/slices';
import { buildFormData, buildRequestData } from '../helpers';
import { useAutomationService } from '../services';
import { Automation, AutomationFormData } from '../types';

export interface AutomationFormDialogController {
  isOpen: boolean;
  initFormData: AutomationFormData | null;
  error: ApiError | null;
  open: (automation?: Automation) => void;
  close: () => void;
  submit: (
    full: AutomationFormData,
    nextAction?: () => Promise<unknown>,
  ) => Promise<void>;
}

export const useAutomationFormDialogController = (): AutomationFormDialogController => {
  const currentCompanyId = useSelector(getCurrentCompanyId) as string;
  const [initFormData, setInitFormData] = useState<AutomationFormData | null>(null);

  const { createAutomation, updateAutomation, error } = useAutomationService();

  const open = useCallback((automation?: Automation) => {
    setInitFormData(buildFormData(automation));
  }, []);

  const close = useCallback(() => {
    setInitFormData(null);
  }, []);

  const submit = useCallback(
    async (full: AutomationFormData, nextAction?: () => Promise<unknown>) => {
      const { id: automationId } = full;
      const payload = buildRequestData(full);

      try {
        if (automationId) {
          await updateAutomation(currentCompanyId, automationId, payload);
        } else {
          await createAutomation(currentCompanyId, payload);
        }

        close();
        await nextAction?.();
      } catch {
        // error surfaced via redux state (drives errorList); keep dialog open
      }
    },
    [createAutomation, updateAutomation, close, currentCompanyId],
  );

  return {
    isOpen: !!initFormData,
    initFormData,
    error,
    open,
    close,
    submit,
  };
};
